import { Dispatch, SetStateAction } from 'react';
import BN from 'bignumber.js';
import { simpleEncode, rawDecode } from 'ethereumjs-abi';

import NativeCurrency from './NativeCurrency';

import chains, { Chain } from './config/chains';

declare global {
  interface Window {
    ethereum: any;
  }
}

class Wallet {
  // local varicable
  private provider: any;
  private addressProcesser: Dispatch<SetStateAction<string>>;
  private chainProcesser: Dispatch<SetStateAction<string>>;
  private address: string;
  private chain: string;

  // constructor
  constructor(
    addressProcesser: Dispatch<SetStateAction<string>>,
    chainProcesser: Dispatch<SetStateAction<string>>,
    address: string = '',
    chain: string = '',
  ) {
    this.addressProcesser = addressProcesser;
    this.chainProcesser = chainProcesser;
    this.address = address;
    this.chain = chain;
  }

  // instance function
  public initialize(): void {
    this.initializeProvider();
    this.initializeAddress();
    this.initializeChain();
  }
  public initializeProvider(): void {
    try {
      this.provider.removeAllListeners();
    } catch (error) {
      /**/
    }
    this.provider = undefined;
  }
  public initializeAddress(): void {
    this.setAddress('');
  }
  public initializeChain(): void {
    this.setChain('');
  }
  public async connectWallet(): Promise<string> {
    if (!window.ethereum) {
      throw new Error("There's no wallet");
    }

    if (!this.provider) {
      this.provider = window.ethereum;

      this.provider.on('connect', () => this.connected());
      this.provider.on('disconnect', () => this.disconnected());
      this.provider.on('chainChanged', () => this.chainChanged());
      this.provider.on('accountsChanged', () => this.accountsChanged());
    }

    // sync address
    const accounts = (await this.callRequestAccounts()) || [];
    const account = accounts[0] || '';
    this.setAddress(account);

    // sync chain
    const chain = await this.callChain();
    this.setChain(Wallet.parseChainID(chain));

    return account;
  }

  public callRequestAccounts(): Promise<any> {
    return this.rawCall([], 'eth_requestAccounts') || [];
  }
  public callChain(): Promise<any> {
    return this.rawCall([], 'net_version') || [];
  }
  public callWatchAsset(
    address: string,
    symbol: string,
    type: string = 'ERC20',
    decimals: number = 18,
    image: string = '',
  ): Promise<any> {
    const params = {
      type,
      options: {
        address,
        symbol,
        decimals,
        image: image || undefined,
      },
    };

    return this.rawCall(params, 'wallet_watchAsset');
  }
  public callAddChain(
    chainId: string,
    chainName: string,
    rpcUrls: string[],
    blockExplorerUrls: string[] | undefined = undefined,
    nativeCurrency: NativeCurrency | undefined = undefined,
    iconUrls: string[] | undefined = undefined,
  ): Promise<any> {
    const params = [
      {
        chainId,
        chainName,
        rpcUrls,
        blockExplorerUrls,
        nativeCurrency,
        iconUrls,
      },
    ];

    return this.rawCall(params, 'wallet_addEthereumChain');
  }

  public async contractCall(
    to: string,
    signature: string,
    args: any[] = [],
    outputs: string[] = [],
    from: string = this.getAddress(),
  ): Promise<any> {
    const data: string = Wallet.makeData(signature, args || []);
    const params = [
      {
        from,
        to,
        data,
        value: '0x0',
      },
      'latest',
    ];
    const rawResponse = await this.rawCall(params);
    const bufferResponse = Wallet.hexToBuffer(rawResponse);
    const decodedResponse = rawDecode(outputs, bufferResponse);

    return decodedResponse;
  }
  public async rawCall(
    params: any[] | object,
    method: string = 'eth_call',
    from: string = this.getAddress(),
  ): Promise<any> {
    if (!this.provider) {
      throw new Error("Wallet does'nt connected");
    }

    const requestObject = {
      params,
      method,
      from,
    };
    const response = await this.provider.request(requestObject);

    if (!response || response === '0x') {
      throw new Error('No response');
    }

    return response;
  }

  public sendWithSignature(
    to: string,
    signature: string,
    args: any[] = [],
    value: string = '0x0',
    from: string = this.getAddress(),
  ): Promise<string> {
    return this.send(to, Wallet.makeData(signature, args || []), value, from);
  }
  public send(
    to: string,
    data: string,
    value: string = '0x0',
    from: string = this.getAddress(),
  ): Promise<string> {
    const transactionParameter = {
      from,
      to,
      value,
      data,
    };
    const params = [transactionParameter];

    return this.rawSend(params);
  }
  public async rawSend(
    params: any[] | object,
    method: string = 'eth_sendTransaction',
    from: string = this.getAddress(),
  ): Promise<string> {
    if (!(this.provider && this.address && this.chain)) {
      throw new Error("Wallet does'nt connected");
    }

    const requestObject = {
      params,
      method,
      from,
    };
    const receipt: string = await this.provider.request(requestObject);

    if (!receipt || `${receipt}`.length < 2 || receipt === '0x') {
      throw new Error('No receipt');
    }

    return receipt;
  }

  // wallet event
  private connected(): void {
    this.connectWallet().catch((error) => console.error('connected error', error));
  }
  private disconnected(): void {
    this.initialize();
  }
  private chainChanged(): void {
    this.initializeChain();
    this.connectWallet().catch((error) => console.error('chainChanged error', error));
  }
  private accountsChanged(): void {
    this.initializeAddress();
    this.connectWallet().catch((error) => console.error('accountsChanged error', error));
  }

  // getter
  public getAddress(): string {
    return this.address;
  }
  public getchain(): string {
    return this.chain;
  }
  // setter
  public setAddress(address: string): void {
    this.address = '';

    if (address && Wallet.isAddress(address)) {
      this.address = address;
    }

    this.addressProcesser(this.address);
  }
  public setChain(chain: string): void {
    this.chain = '';

    if (chain && Wallet.isAllowedChain(chain)) {
      this.chain = chain;
    } else {
      const recommendChain = Wallet.getAllowedChains()[0];

      if (recommendChain instanceof Chain) {
        const { id, name, rpcUrl, symbol } = recommendChain;
        const nativeCurrency = new NativeCurrency(symbol, symbol);

        this.callAddChain(id, name, [rpcUrl], undefined, nativeCurrency).catch((error) =>
          console.error('recommend chain', error),
        );
      }
    }

    this.chainProcesser(this.chain);
  }

  // class variable
  public static regexAddress: RegExp = /^(0x)?[0-9a-fA-F]{40}$/i;

  // class function
  public static isAddress(address: string): boolean {
    return Wallet.regexAddress.test(address);
  }
  public static makeData(signature: string, args: any[]): string {
    return `0x${simpleEncode(signature, ...(args || [])).toString('hex')}`;
  }
  public static hexToBuffer(hex: string): Buffer {
    return Buffer.from(hex.replace('0x', ''), 'hex');
  }
  public static bufferToHex(buffer: Buffer): string {
    return `0x${
      Array.prototype.map
        .call(new Uint8Array(buffer), (x: any) => `00${x.toString(16)}`.slice(-2))
        .join('') || '0'
    }`;
  }
  public static bn2Str(value: BN, isInteger: boolean = false): string {
    let result = value.toFixed(100);

    const dotIndex = result.indexOf('.');

    if (dotIndex > 0 && dotIndex < result.length) {
      if (isInteger) {
        result = result.slice(0, dotIndex);
      } else {
        result = result.replace(/0*$/, '');

        if (dotIndex + 1 === result.length) {
          result = result.slice(0, dotIndex);
        }

        if (result.length === 0) {
          result = '0';
        }
      }
    } else if (dotIndex === 0) {
      result = '0';
    }

    if (result === '-0') {
      result = '0';
    }

    return result;
  }
  public static bn2Hex(value: BN, isInteger: boolean = false): string {
    let result = '0x0';

    if (isInteger) {
      result = `0x${new BN(Wallet.bn2Str(value, true)).toString(16)}`;
    } else {
      result = `0x${value.toString(16)}`;
    }

    return result;
  }
  public static getChain(id: any): Chain | undefined {
    return chains.find((chain) => chain.id === id);
  }
  public static getChainByNumber(id: number): Chain | undefined {
    return chains.find((chain) => chain.numberID === id);
  }
  public static parseChainID(id: any): string {
    return (Wallet.getChainByNumber(parseInt(id)) || {}).id || '';
  }
  public static isAllowedChain(id: string): boolean {
    return (Wallet.getChain(id) || {}).allowed || false;
  }
  public static getAllowedChains(): Chain[] {
    return chains.filter((chain) => chain.allowed);
  }
  public static getChainName(id: string): string {
    return (Wallet.getChain(id) || {}).name || '';
  }
  public static getChainColor(id: string): string {
    return (Wallet.getChain(id) || {}).color || '';
  }
}

export default Wallet;
